"use client";

import { Tabs } from "@/components/ui/tabs";
import Image from 'next/image';
import { CTAAnimation, MissionAnimation, VisionAnimation } from "@/components/ui/lottie-animation";
import Link from 'next/link';
import { CheckCircle, Rocket, Users, Search, MessageCircleQuestion, Linkedin, Twitter, Github } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { SpotlightCard } from "@/components/ui/spotlight-card";

const tabs = [
  {
    title: "Mission",
    value: "mission",
    content: (
      <div className="w-full overflow-hidden relative h-full rounded-2xl p-10  font-bold text-white bg-gradient-to-br from-background  to-secondary border border-primary/40">
        <h2 className="text-3xl md:text-4xl font-bold font-headline">Our Mission: AI for Everyone</h2>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              Our mission is simple: to make powerful AI accessible and affordable for the small and medium-sized businesses that form the backbone of our communities. We saw a gap where enterprise-level AI solutions were out of reach for SMMBs, and we decided to change that.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Tellis Technology was born from a digital marketing agency. We realized that our clients' biggest challenges—inefficiency, missed leads, and being overwhelmed by repetitive tasks—could be solved more effectively with AI. We pivoted to focus exclusively on building practical, no-code AI agents and automations that deliver immediate value.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>Democratizing access to powerful AI tools.</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>Helping local businesses compete and thrive.</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>Focusing on results, not just technology.</span>
              </li>
            </ul>
          </div>
          <MissionAnimation />
        </div>

      </div>
    ),
  },
  {
    title: "Vision",
    value: "vision",
    content: (
      <div className="w-full overflow-hidden relative h-full rounded-2xl p-10  font-bold text-white bg-gradient-to-br from-background  to-secondary border border-primary/40">

        <h2 className="text-3xl md:text-4xl font-bold font-headline">Our Vision: A Future Where Every Business Runs on AI        </h2>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              Our vision is to build a world where AI automation is no longer a luxury for big corporations, but a practical, everyday tool for businesses. We believe that when AI is accessible, affordable, and simple to use, it can unlock new levels of productivity and business growth. </p>
            <p className="text-muted-foreground leading-relaxed">
              We imagine a future where a local service provider can run 24/7 customer support, schedule appointments, and manage operations — all with custom AI agents tailored to their business needs. By placing AI directly into the hands of SMB owners, we aim to bridge the digital gap and fuel a new era of innovation at every business size.

            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>
                  Empowering businesses worldwide to thrive with AI-driven operations</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>Bridging the technology gap for small and medium enterprises</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>Setting the standard for no-code, scalable, AI-powered business tools.</span>
              </li>
            </ul>
          </div>
          <VisionAnimation />
        </div>

      </div>
    ),
  },

]

const teamMembers = [
  {
    name: 'Alex Johnson', role: 'Founder & AI Strategist', image: '/team1.jpg', hint: 'man smiling', social: [
      { icon: 'linkedin', url: 'https://www.linkedin.com/in/alex-johnson' },
      { icon: 'twitter', url: 'https://www.twitter.com/alexjohnson' },
      { icon: 'github', url: 'https://www.github.com/alexjohnson' },
    ]
  },
  {
    name: 'Samantha Lee', role: 'Lead Automation Engineer', image: '/team2.jpg', hint: 'woman smiling', social: [
      { icon: 'linkedin', url: 'https://www.linkedin.com/in/samantha-lee' },
      { icon: 'twitter', url: 'https://www.twitter.com/samanthalee' },
      { icon: 'github', url: 'https://www.github.com/samanthalee' },
    ]
  },
  {
    name: 'David Chen', role: 'Head of Client Solutions', image: '/team3.jpg', hint: 'man glasses', social: [
      { icon: 'linkedin', url: 'https://www.linkedin.com/in/david-chen' },
      { icon: 'twitter', url: 'https://www.twitter.com/davidchen' },
      { icon: 'github', url: 'https://www.github.com/davidchen' },
    ]
  },
  {
    name: 'Maria Garcia', role: 'Senior AI Consultant', image: '/team4.jpg', hint: 'woman professional', social: [
      { icon: 'linkedin', url: 'https://www.linkedin.com/in/maria-garcia' },
      { icon: 'twitter', url: 'https://www.twitter.com/mariagarcia' },
      { icon: 'github', url: 'https://www.github.com/mariagarcia' },
    ]
  },
];

const values = [
  { title: 'Client-Centric', description: 'Your success is our success. We deeply invest in understanding your business to deliver solutions that drive real results.' },
  { title: 'Innovation', description: 'We stay at the forefront of AI technology to bring you cutting-edge, effective, and reliable solutions.' },
  { title: 'Simplicity', description: 'We believe powerful technology should be accessible. Our focus is on no-code solutions that are easy for you and your team to use.' },
  { title: 'Integrity', description: 'We build relationships on trust and transparency, ensuring we are a partner you can count on for the long term.' },
];

const images1 = [
  { src: '/WhatsApp Image 2025-07-12 at 1.56.41 PM (1).jpeg', alt: 'Team at work' },
  { src: '/WhatsApp Image 2025-07-12 at 1.56.43 PM.jpeg', alt: 'Workspace' },
  { src: '/WhatsApp Image 2025-07-12 at 1.56.43 PM (2).jpeg', alt: 'Quote Wall' },
];
const images2 = [
  { src: '/WhatsApp Image 2025-07-12 at 1.56.44 PM.jpeg', alt: 'Plant decor' },
  { src: '/WhatsApp Image 2025-07-12 at 1.56.45 PM (1).jpeg', alt: 'Meeting' },
  { src: '/WhatsApp Image 2025-07-12 at 1.56.46 PM.jpeg', alt: 'Workstation' },
];

const whyChooseUsFeatures = [
  {
    icon: Rocket,
    title: 'Fast Service',
    description: 'Optimum fast services for your businesses.'
  },
  {
    icon: Users,
    title: 'Team Work',
    description: 'Excellent team work to boost your businesses.'
  },
  {
    icon: Search,
    title: 'Transparency',
    description: 'Extremely transparent for greater businesses to grow rapidly.'
  },
  {
    icon: MessageCircleQuestion,
    title: '24/7 Support',
    description: 'Always online to help businesses that do not get neglected.'
  }
];

export default function AboutUsPage() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    setIsSubmitting(true);

    try {
      console.log('Sending request to /api/demo...');
      const response = await fetch('/api/demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Your demo request has been submitted successfully. We'll get back to you soon!",
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to submit demo request. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to submit demo request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className=" bg-secondary/50 py-20 md:py-28 h-[20rem] flex items-center justify-center  ">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold font-headline text-primary mb-4">About Tellis Technology</h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            We are a team of innovators and problem-solvers dedicated to empowering small and medium-sized businesses with the transformative power of AI.
          </p>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="w-full py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <span className="text-accent font-semibold tracking-wider uppercase">---Why Choose Us---</span>
                <h2 className="text-3xl md:text-4xl font-bold font-headline mt-2">Why People Always Prefer Tellis Technologies</h2>
                <p className="text-muted-foreground mt-4 leading-relaxed">
                  Tellis Technologies is an AI-driven 24/7 inbound call center based in Siliguri, West Bengal, India, empowering businesses to handle customer communication with greater speed, intelligence, and efficiency. By blending human expertise with cutting-edge AI technology, we serve as the intelligent voice of businesses that choose to outsource their telephone and data communications</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {whyChooseUsFeatures.map((feature) => (
                  <div key={feature.title} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-accent" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold font-headline">{feature.title}</h3>
                      <p className="text-muted-foreground mt-1">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Image
                src="/teamAboutUs.avif"
                alt="Customer service representative smiling"
                width={600}
                height={600}
                className="rounded-lg shadow-md"
                data-ai-hint="customer service"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 items-center gap-5">
          {/* Text Block */}
          <div>
            <div className="flex flex-col items-center justify-center pb-20">
              <div className="animate-fade-in">
                <h2 className="text-4xl font-bold mb-4 text-foreground">Our Story</h2>
                <p className="text-muted-foreground max-w-2xl mb-6">
                  We started as a digital marketing agency helping small and medium businesses grow online.
                  Over time, we noticed they struggled with inefficiencies, missed leads, and repetitive tasks.
                  Discovering the power of AI automation, we realized it could transform their operations instantly.</p>
                <p className="text-muted-foreground max-w-3xl">
                  Recognizing this, we pivoted to become a dedicated AI solutions company.
                  Today, we build practical, no-code AI agents for support, scheduling, and workflow automation.
                  Our mission is to make enterprise-grade AI accessible and affordable for every business.

                </p>
              </div>
            </div>
            {/* Image Grid - Asymmetric Layout */}
            <div className="grid grid-cols-2 grid-rows-2 gap-4 h-[500px] ">
              <div className="row-span-2 col-span-1 overflow-hidden rounded-xl animate-fade-in py-20">
                <Image
                  src={images1[0].src}
                  alt={images1[0].alt}
                  width={500}
                  height={500}
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="row-span-1 col-span-1 overflow-hidden rounded-xl animate-fade-in">
                <Image
                  src={images1[1].src}
                  alt={images1[1].alt}
                  width={500}
                  height={500}
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="row-span-1 col-span-1 overflow-hidden rounded-xl animate-fade-in">
                <Image
                  src={images1[2].src}
                  alt={images1[2].alt}
                  width={500}
                  height={500}
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
          </div>

          {/* Image Grid2 - Asymmetric Layout */}
          <div>
            <div className="grid grid-cols-2 grid-rows-2 gap-4 h-[500px] ">
              <div className="row-span-2 col-span-1 overflow-hidden rounded-xl animate-fade-in py-20">
                <Image
                  src={images2[0].src}
                  alt={images2[0].alt}
                  width={500}
                  height={500}
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="row-span-1 col-span-1 overflow-hidden rounded-xl animate-fade-in">
                <Image
                  src={images2[1].src}
                  alt={images2[1].alt}
                  width={500}
                  height={500}
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="row-span-1 col-span-1 overflow-hidden rounded-xl animate-fade-in">
                <Image
                  src={images2[2].src}
                  alt={images2[2].alt}
                  width={500}
                  height={500}
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
            {/* Text Block */}
            <div className="flex flex-col items-center justify-center">
              <div className="mt-0 animate-fade-in">
                <h3 className="text-3xl font-semibold text-foreground mb-4">Our Workplace</h3>
                <p className="text-muted-foreground max-w-3xl mb-4">
                  At Tellis Technologies, we’ve built a collaborative, inclusive, and innovative environment.
                  Our workplace encourages open ideas, creative thinking, and a shared mission to solve real problems.
                  It’s a space where diverse minds come together to build AI solutions that truly make a difference.
                </p>
                <p className="text-muted-foreground max-w-3xl">
                  From brainstorming sessions to strategy workshops, every voice matters here.
                  We believe great work happens when people feel valued, connected, and inspired.
                  Our office culture reflects our values — human-focused, agile, and always moving forward.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section >


      {/* Mission & Vision Section */}
      <section className="bg-background h-[55rem]">
        <div className="h-[43rem] [perspective:1000px] relative b flex flex-col max-w-5xl mx-auto w-full  items-start justify-start">
          <Tabs tabs={tabs} />

        </div>

      </section>
      {/* Our Values Section
      <section className="w-full py-16 md:py-24 bg-secondary/50" >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-headline">Our Core Values</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
              The principles that guide our work and our partnership with you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <Card key={value.title}>
                <CardHeader>
                  <CardTitle className="font-headline text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section > */}

      {/* Meet the Team Section */}
      < section className="w-full py-16 md:py-24 bg-secondary/50" >
        <div className="container mx-auto max-w-7xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-headline mb-12">Our Team</h2>
          
          {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div key={member.name} className="relative group overflow-hidden rounded-xl shadow-lg" >

                <div className="w-full h-96 overflow-hidden relative">
                  <Image
                    src={member.image}
                    alt={member.name}
                    fill
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                  />
                </div>

                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white font-body text-5xl px-4 py-1 rounded-md opacity-0 group-hover:opacity-100 transition duration-300">
                  <h3 className="text-sm font-semibold">{member.name}</h3>
                </div>

                
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                  <div className="flex space-x-3 mb-1">
                    {member.social.map((social, idx) => (
                      <a key={idx} href={social.url} className="hover:text-purple-400">
                        {social.icon === 'linkedin' && <Linkedin className="w-4 h-4" />}
                        {social.icon === 'twitter' && <Twitter className="w-4 h-4" />}
                        {social.icon === 'github' && <Github className="w-4 h-4" />}
                      </a>
                    ))}
                  </div>
                  <p className="text-accent">{member.role}</p>
                </div>
              </div>
            ))}
           </div> */}


          <div className="flex flex-col items-center justify-center p-10 w-full h-[40rem] mt-20">
            <Image
              src="/squad.jpg"
              alt="Team"
              width={1400}
              height={600}
              className="rounded-lg shadow-md h-[40rem]"
            />
          </div>
        </div>
      </ section>

      {/* CTA Section */}
      <section className="w-full py-16 md:py-24 bg-gradient-to-b from-background to-secondary " >
        <div className="container mx-auto px-4  grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="container mx-auto px-4  ">
            <h2 className="text-3xl md:text-5xl font-bold font-headline mb-4">Ready to Transform Your Business with AI?</h2>
            <CTAAnimation />
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Discover how Tellis Technologies can help your business save time, reduce costs, and stay ahead with custom AI solutions built for small and medium-sized businesses.
              Our experts are here to discuss your goals and design a strategy that works for you.</p>
            <Button asChild size="lg" variant="secondary">
              <Link href="/contact">Explore Our Services</Link>
            </Button>
          </div>
          <SpotlightCard>
            <div className="container mx-auto p-10 rounded-xl">
              <h2 className="text-3xl md:text-4xl font-bold font-headline mb-4">Book a Demo</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john.doe@example.com"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your Company Inc."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">How can we help you?</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project or business needs..."
                    rows={6}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full sm:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </SpotlightCard>
        </div>
      </section >
    </div >
  );
}
